import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Clock, Users, DollarSign, CheckCircle, XCircle, 
  AlertCircle, RefreshCw, Search, Filter, Video, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminLiveStudio = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, confirmed, pending, cancelled
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/live-studio/admin/bookings`);
      if (res.ok) {
        const data = await res.json();
        setBookings(data.bookings || []);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      const res = await fetch(`${API}/api/live-studio/admin/bookings/${bookingId}/cancel`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Booking cancelled');
        fetchBookings();
      } else {
        toast.error('Failed to cancel booking');
      }
    } catch (err) {
      toast.error('Error cancelling booking');
    }
  };

  const confirmBooking = async (bookingId) => {
    try {
      const res = await fetch(`${API}/api/live-studio/bookings/${bookingId}/confirm`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Booking confirmed');
        fetchBookings();
      } else {
        toast.error('Failed to confirm booking');
      }
    } catch (err) {
      toast.error('Error confirming booking');
    }
  };

  const filteredBookings = bookings
    .filter(b => filter === 'all' || b.status === filter)
    .filter(b => 
      searchTerm === '' || 
      b.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.customer_email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const stats = {
    total: bookings.length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    pending: bookings.filter(b => b.status === 'pending').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
    revenue: bookings
      .filter(b => b.status === 'confirmed')
      .reduce((sum, b) => sum + (b.price || 0), 0)
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Video className="w-8 h-8 text-green-500" />
              Live Studio Bookings
            </h1>
            <p className="text-gray-400 mt-1">Manage live recording session bookings</p>
          </div>
          <div className="flex gap-3">
            <a
              href="/admin/live-studio/sessions"
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-lg flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              Session Manager (Zoom-style)
            </a>
            <a
              href="/admin/live-studio/host"
              className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg flex items-center gap-2"
            >
              <Video className="w-4 h-4" />
              Host Dashboard
            </a>
            <button
              onClick={fetchBookings}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-[#141414] rounded-xl p-4 border border-gray-800">
            <div className="text-gray-400 text-sm">Total Bookings</div>
            <div className="text-2xl font-bold mt-1">{stats.total}</div>
          </div>
          <div className="bg-[#141414] rounded-xl p-4 border border-green-500/30">
            <div className="text-green-400 text-sm">Confirmed</div>
            <div className="text-2xl font-bold mt-1 text-green-500">{stats.confirmed}</div>
          </div>
          <div className="bg-[#141414] rounded-xl p-4 border border-amber-500/30">
            <div className="text-amber-400 text-sm">Pending</div>
            <div className="text-2xl font-bold mt-1 text-amber-500">{stats.pending}</div>
          </div>
          <div className="bg-[#141414] rounded-xl p-4 border border-red-500/30">
            <div className="text-red-400 text-sm">Cancelled</div>
            <div className="text-2xl font-bold mt-1 text-red-500">{stats.cancelled}</div>
          </div>
          <div className="bg-[#141414] rounded-xl p-4 border border-green-500/30">
            <div className="text-green-400 text-sm">Revenue</div>
            <div className="text-2xl font-bold mt-1 text-green-500">${stats.revenue}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-[#141414] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'confirmed', 'pending', 'cancelled'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg capitalize transition-all ${
                  filter === f
                    ? 'bg-green-500 text-black font-semibold'
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Bookings Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No bookings found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <motion.div
                key={booking.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#141414] rounded-xl p-6 border border-gray-800 hover:border-gray-700 transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Customer Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center text-black font-bold">
                        {booking.customer_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <h3 className="font-semibold">{booking.customer_name}</h3>
                        <p className="text-sm text-gray-400">{booking.customer_email}</p>
                      </div>
                    </div>
                    {booking.notes && (
                      <p className="text-sm text-gray-500 mt-2 italic">"{booking.notes}"</p>
                    )}
                  </div>

                  {/* Session Details */}
                  <div className="flex flex-wrap gap-4 items-center">
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Date</div>
                      <div className="font-semibold flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-green-500" />
                        {booking.date}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Time</div>
                      <div className="font-semibold flex items-center gap-1">
                        <Clock className="w-4 h-4 text-green-500" />
                        {booking.time}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Duration</div>
                      <div className="font-semibold">{booking.duration}h</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Price</div>
                      <div className="font-semibold text-green-500">${booking.price}</div>
                    </div>
                  </div>

                  {/* Status & Actions */}
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(booking.status)}`}>
                      {booking.status?.toUpperCase()}
                    </span>
                    
                    {booking.status === 'pending' && (
                      <button
                        onClick={() => confirmBooking(booking.id)}
                        className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400"
                        title="Confirm Booking"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                    )}
                    
                    {booking.status !== 'cancelled' && (
                      <button
                        onClick={() => cancelBooking(booking.id)}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400"
                        title="Cancel Booking"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLiveStudio;
